import { useState } from "react";
import { motion } from "framer-motion";
import { Search, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const SUGGESTIONS = [
  "How addictive is methamphetamine compared to other drugs?",
  "What does meth do to the brain long term?",
  "What are the early warning signs of meth use?",
  "How effective is treatment for meth addiction?",
];

export default function Evidence() {
  const [question, setQuestion] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [offline, setOffline] = useState(false);

  const ask = async (q) => {
    const query = (q || question).trim();
    if (query.length < 3) return;
    setBusy(true);
    setResult(null);
    setOffline(false);
    try {
      const { data } = await api.post("/evidence", { question: query });
      setResult(data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 503) setOffline(true);
      else toast.error(formatApiError(err.response?.data?.detail, "Research service is busy — try again shortly."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="evidence" className="bg-[#F5F2EC] text-[#1A1A1A] py-24 sm:py-32 border-t border-[#E2DCD2]">
      <div className="max-w-4xl mx-auto px-6">
        <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mono-label text-[#C2410C] mb-4">Ask the evidence</motion.p>
        <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="font-display font-extrabold uppercase text-3xl sm:text-5xl tracking-tight mb-4">
          Research, with receipts
        </motion.h2>
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-[#52525B] mb-8 max-w-2xl">
          Ask a question about methamphetamine and get an answer grounded in current published research — with the sources linked. Educational information, not medical advice.
        </motion.p>

        <div className="flex flex-wrap gap-2 mb-5">
          {SUGGESTIONS.map((s) => (
            <button key={s} data-testid={`evidence-chip-${SUGGESTIONS.indexOf(s)}`} onClick={() => { setQuestion(s); ask(s); }}
              className="text-xs border border-[#E2DCD2] hover:border-[#1E4B3E] hover:text-[#1E4B3E] rounded-full px-4 py-1.5 transition-colors bg-white">
              {s}
            </button>
          ))}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2 mb-8">
          <input value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={1000}
            placeholder="e.g. How does meth affect sleep and mental health?" data-testid="evidence-input"
            className="flex-1 bg-white border border-[#E2DCD2] rounded-full px-5 py-3 text-sm focus:outline-none focus:border-[#1E4B3E]" />
          <button type="submit" disabled={busy} data-testid="evidence-ask-button"
            className="flex items-center gap-2 bg-[#1E4B3E] hover:bg-[#16382e] disabled:opacity-50 text-white font-bold rounded-full px-6 py-3 text-sm transition-colors">
            <Search size={15} /> {busy ? "Researching…" : "Ask"}
          </button>
        </form>

        {offline && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900" data-testid="evidence-offline-note">
            The live research service is being connected. In the meantime, trusted starting points: the NZ Drug Foundation (drugfoundation.org.nz), the Alcohol &amp; Drug Helpline (0800 787 797), and health.govt.nz.
          </div>
        )}

        {result && (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-[#E2DCD2] rounded-2xl p-7" data-testid="evidence-result">
            <p className="text-sm leading-relaxed whitespace-pre-wrap text-[#1A1A1A]">{result.answer.replace(/\*\*(.+?)\*\*/g, "$1")}</p>
            {result.citations?.length > 0 && (
              <div className="mt-5 pt-4 border-t border-[#E2DCD2]">
                <p className="mono-label text-[#52525B] mb-3">Sources</p>
                <ol className="space-y-1.5">
                  {result.citations.map((url, i) => (
                    <li key={i} className="text-xs">
                      <a href={url} target="_blank" rel="noreferrer" className="text-[#1E4B3E] hover:underline inline-flex items-center gap-1 break-all">
                        [{i + 1}] {url} <ExternalLink size={10} className="shrink-0" />
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <p className="mt-4 text-[11px] text-[#52525B]">Educational information only — not medical advice. If someone is in immediate danger, call 111.</p>
          </motion.div>
        )}
      </div>
    </section>
  );
}
